import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { getGroupsDb } from "../../services/groups.api";
import { listFiles, uploadSingleFile } from "../../services/files.api";
import { listWhatsapps } from "../../services/whatsapp.api";
import {
  getGroupCampaign,
  createGroupCampaign,
  updateGroupCampaign,
  uploadGroupCampaignMedia,
} from "../../services/groupCampaigns.api";
import moment from "moment-timezone";
import { getCampaignTimezoneSync } from "../../helpers/getCampaignTimezone";
import { AuthContext } from "../../context/Auth/AuthContext";

// D8.13.3 — Lógica (47 estados + effects + handlers + API) de GroupCampaignModal.
export default function useGroupCampaign({ open, onClose, campaignId, initialValues, onSave, campaignType = null }) {
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [files, setFiles] = useState([]); // NEW: Lista de archivos disponibles
  const [attachment, setAttachment] = useState(null); // NEW: Archivo adjuntado localmente
  const attachmentFile = useRef(null); // NEW: Referencia al input de archivo
  const [campaignMedia, setCampaignMedia] = useState(null); // NEW: Archivo de campaña existente
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [copiedGroupsData, setCopiedGroupsData] = useState([]); // ✅ NEW: Datos de grupos copiados para mostrar
  const [hasInitialized, setHasInitialized] = useState(false); // Control de inicialización
  const [confirmModalOpen, setConfirmModalOpen] = useState(false); // NEW: Modal de confirmación
  const [pendingSubmit, setPendingSubmit] = useState(null); // NEW: Datos pendientes de submit
  // ✅ NUEVO (09/12/2025): Estados para modales de confirmación
  const [confirmChangeScheduledDateModalOpen, setConfirmChangeScheduledDateModalOpen] = useState(false);
  // ✅ NUEVO (10/12/2025): Modal de error cuando falta scheduledDate para agregar horarios
  const [missingScheduledDateModalOpen, setMissingScheduledDateModalOpen] = useState(false);
  const [confirmDeleteAllSchedulesModalOpen, setConfirmDeleteAllSchedulesModalOpen] = useState(false);
  const [confirmDisableHourlyScheduleModalOpen, setConfirmDisableHourlyScheduleModalOpen] = useState(false);
  const [confirmChangeRecurrenceTypeModalOpen, setConfirmChangeRecurrenceTypeModalOpen] = useState(false);
  const [confirmDisableRecurrenceModalOpen, setConfirmDisableRecurrenceModalOpen] = useState(false);
  const [pendingScheduledDateChange, setPendingScheduledDateChange] = useState(null); // Para guardar el cambio pendiente de scheduledDate
  const [pendingSchedulesCount, setPendingSchedulesCount] = useState(0); // Para mostrar cantidad de horarios en el modal
  const [pendingOldHour, setPendingOldHour] = useState(null); // Para mostrar hora antigua en el modal
  const [pendingNewHour, setPendingNewHour] = useState(null); // Para mostrar hora nueva en el modal
  const [pendingRecurrenceTypeChange, setPendingRecurrenceTypeChange] = useState(null); // Para guardar el cambio pendiente de recurrenceType
  const [pendingOldRecurrenceType, setPendingOldRecurrenceType] = useState(null); // Para mostrar tipo anterior
  const [pendingNewRecurrenceType, setPendingNewRecurrenceType] = useState(null); // Para mostrar tipo nuevo
  // ✅ NUEVO (09/12/2025): Ref para acceder a setFieldValue desde fuera del contexto de Formik
  const formikSetFieldValueRef = useRef(null);
  // ✅ CORRECCIÓN: Ref para acceder a resetForm desde fuera del contexto de Formik
  const formikResetFormRef = useRef(null);
  const [searchGroupTerm, setSearchGroupTerm] = useState("");
  const [groupPage, setGroupPage] = useState(1);
  const [messageTab, setMessageTab] = useState(0);
  const [configTab, setConfigTab] = useState(0); // ✅ NUEVO: Pestaña principal (0=MENSAJE, 1=INTERVALO para ÚNICA; 0=MENSAJE, 1=INTERVALO, 2=RECURRENCIA para RECURRENTE)
  const [selectedConnection, setSelectedConnection] = useState("all");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false); // ✅ NEW: Si campaña ya fue completada
  // ✅ NUEVO (08/12/2025): Estado para modal de horarios
  const [hourlyScheduleModalOpen, setHourlyScheduleModalOpen] = useState(false);
  // ✅ NUEVO: Estado para rastrear si el campo de hora fue modificado por el usuario
  const [hourlyScheduleTimeTouched, setHourlyScheduleTimeTouched] = useState(false);
  // ✅ FASE 2: Estados para modales de validación de fecha
  const [datePastModalOpen, setDatePastModalOpen] = useState(false);
  const [datePastMessage, setDatePastMessage] = useState("");
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState(null);
  const [pendingSaveValues, setPendingSaveValues] = useState(null);
  const saveCampaignRef = useRef(null);
  const [shortTimeModalOpen, setShortTimeModalOpen] = useState(false);
  const [shortTimeMessage, setShortTimeMessage] = useState("");
  const [pendingScheduledAt, setPendingScheduledAt] = useState(null);
  const [formatInvalidModalOpen, setFormatInvalidModalOpen] = useState(false);
  const [formatInvalidMessage] = useState("");
  const [sizeExceededModalOpen, setSizeExceededModalOpen] = useState(false);
  // const [sizeExceededMessage, setSizeExceededMessage] = useState(""); // ✅ TODO: Implementar validación de tamaño de archivo
  const [editingScheduleIndex, setEditingScheduleIndex] = useState(null); // null = nuevo, número = editar
  const [hourlyScheduleMessageTab, setHourlyScheduleMessageTab] = useState(0); // Tab separado para modal de horarios
  const [currentSchedule, setCurrentSchedule] = useState({
    time: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    mediaPath: null,
    mediaName: null,
    mediaFile: null
  });
  // ✅ NUEVO: Referencia al input de archivo para modal de horarios
  const hourlyScheduleFileInput = useRef(null);
  const groupsPerPage = 30;

  // ✅ FASE 2: Determinar tipo de campaña (UNICA o RECURRENTE)
  // Extraer valores de initialValues para evitar expresiones complejas en dependencias
  const initialTipo = initialValues?.tipo;
  const initialIsRecurring = initialValues?.isRecurring;
  const initialRecurrenceType = initialValues?.recurrenceType;
  const initialRecurrenceInterval = initialValues?.recurrenceInterval;
  
  const currentCampaignType = React.useMemo(() => {
    if (campaignId && initialValues) {
      // ✅ CORRECCIÓN CRÍTICA: Usar el campo 'tipo' como fuente de verdad principal
      // Si 'tipo' está establecido y es válido, usarlo directamente
      if (initialTipo === 'GRUPO_UNICA' || initialTipo === 'UNICA') {
        return 'UNICA';
      }
      if (initialTipo === 'GRUPO_RECURRENTE' || initialTipo === 'RECURRENTE') {
        return 'RECURRENTE';
      }
      
      // ✅ Si 'tipo' no está establecido, determinar desde otros campos
      // 1. Si isRecurring es explícitamente false, es UNICA
      if (initialIsRecurring === false || initialIsRecurring === 0 || initialIsRecurring === '0') {
        return 'UNICA';
      }
      // 2. Si isRecurring es explícitamente true, es RECURRENTE
      if (initialIsRecurring === true || initialIsRecurring === 1 || initialIsRecurring === '1') {
        return 'RECURRENTE';
      }
      // 3. Si tiene campos de recurrencia configurados, es RECURRENTE
      if (initialRecurrenceType || initialRecurrenceInterval) {
        return 'RECURRENTE';
      }
      // 4. Por defecto, es UNICA
      return 'UNICA';
    }
    // Si no hay campaignId, usar el campaignType del prop
    return campaignType || 'UNICA';
  }, [campaignId, initialValues, initialTipo, initialIsRecurring, initialRecurrenceType, initialRecurrenceInterval, campaignType]);

  // ✅ FASE 2: Inicializar configTab según el tipo de campaña (solo al abrir el modal o cambiar tipo)
  useEffect(() => {
    if (open) {
      // Solo resetear cuando el modal se abre o cambia el tipo de campaña
      // NO resetear cuando configTab cambia (permite navegar entre pestañas)
      if (currentCampaignType === 'RECURRENTE') {
        // Para RECURRENTE: 0=MENSAJE, 1=INTERVALO, 2=RECURRENCIA
        setConfigTab(0); // Empezar en MENSAJE
      } else {
        // Para ÚNICA: 0=MENSAJE, 1=INTERVALO
        setConfigTab(0); // Empezar en MENSAJE
      }
      setMessageTab(0); // Resetear tab de mensajes
    }
  }, [open, currentCampaignType]); // ✅ CORRECCIÓN: Eliminado configTab de dependencias para evitar ciclo infinito

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);


  // Cargar conexiones desde API
  useEffect(() => {
    const loadWhatsapps = async () => {
      if (whatsapps.length > 0) return; // Ya cargadas
      
      try {
        const response = await listWhatsapps({ session: 0 });
        if (response.data && Array.isArray(response.data)) {
          setWhatsapps(response.data);
          
          // ✅ NUEVO: Si es una copia y aún no se ha establecido el connection, hacerlo aquí
          if (!campaignId && initialValues?.whatsappId && selectedConnection === "all") {
            const whatsappIdValue = initialValues.whatsappId.toString();
            const whatsappExists = whatsapps.some(w => w.id.toString() === whatsappIdValue);
            if (whatsappExists) {
              setSelectedConnection(whatsappIdValue);
            }
          }
        }
      } catch (err) {
        console.error('Error cargando conexiones:', err);
        toastError(err);
        setWhatsapps([]);
      }
    };
    
    if (open) {
      loadWhatsapps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cargar archivos disponibles
  useEffect(() => {
    const loadFiles = async () => {
      if (!open) return;
      
      try {
        const { data } = await listFiles({ companyId });
        setFiles(data.files || []);
      } catch (err) {
        console.error('Error cargando archivos:', err);
        toastError(err);
        setFiles([]);
      }
    };
    
    loadFiles();
  }, [open, companyId]);

  // ✅ NUEVO: Crear initialState dinámico basado en initialValues
  // ✅ CORRECCIÓN CRÍTICA: Usar useMemo para calcular initialState solo una vez cuando el modal se abre
  // Esto evita que scheduledDate se recalcule en cada render, causando que la hora cambie automáticamente
  const initialState = useMemo(() => {
    
    // ✅ CORRECCIÓN CRÍTICA: Si hay initialValues con id (edición), procesarlos completamente
    if (initialValues && initialValues.id) {
      // ✅ Edición: Usar todos los datos de initialValues
      // ✅ CORRECCIÓN: scheduledDate debe ser string "YYYY-MM-DD HH:mm:ss" en zona empresa
      let scheduledDateValue = "";
      if (initialValues.scheduledDate) {
        scheduledDateValue = String(initialValues.scheduledDate);
      } else if (initialValues.nextScheduledAt && initialValues.isRecurring) {
        // Para campañas RECURRENTES activas, usar nextScheduledAt como scheduledDate
        scheduledDateValue = String(initialValues.nextScheduledAt);
      }
      
      // ✅ Procesar recurrenceDaysOfWeek si existe
      let recurrenceDaysOfWeekValue = "";
      if (initialValues.recurrenceDaysOfWeek) {
        if (Array.isArray(initialValues.recurrenceDaysOfWeek)) {
          recurrenceDaysOfWeekValue = JSON.stringify(initialValues.recurrenceDaysOfWeek);
        } else if (typeof initialValues.recurrenceDaysOfWeek === 'string') {
          recurrenceDaysOfWeekValue = initialValues.recurrenceDaysOfWeek;
        }
      }
      
      return {
        name: initialValues.name || "",
        whatsappId: initialValues.whatsappId || initialValues.whatsapp?.id || "",
        scheduledDate: scheduledDateValue,
        message1: initialValues.message1 || "",
        message2: initialValues.message2 || "",
        message3: initialValues.message3 || "",
        message4: initialValues.message4 || "",
        message5: initialValues.message5 || "",
        messageInterval: initialValues.messageInterval || 45,
        longerIntervalAfter: initialValues.longerIntervalAfter || 20,
        greaterInterval: initialValues.greaterInterval || 60,
        fileListId: initialValues.fileListId || "",
        // ✅ CAMPOS DE RECURRENCIA
        isRecurring: initialValues.isRecurring !== undefined ? initialValues.isRecurring : (initialValues.tipo === 'GRUPO_RECURRENTE' || initialValues.tipo === 'RECURRENTE'),
        recurrenceType: initialValues.recurrenceType || "",
        recurrenceInterval: initialValues.recurrenceInterval || "",
        recurrenceDaysOfWeek: recurrenceDaysOfWeekValue,
        recurrenceDayOfMonth: initialValues.recurrenceDayOfMonth || "",
        recurrenceEndDate: initialValues.recurrenceEndDate || "",
        maxExecutions: initialValues.maxExecutions || "",
        // ✅ CAMPOS DE HORARIOS MÚLTIPLES
        useHourlySchedule: initialValues.useHourlySchedule || false,
        recurrenceHourlySchedules: initialValues.recurrenceHourlySchedules || 
          (initialValues.hourlySchedules && Array.isArray(initialValues.hourlySchedules) 
            ? initialValues.hourlySchedules.map(schedule => ({
                time: schedule.time,
                message1: schedule.message1 || "",
                message2: schedule.message2 || "",
                message3: schedule.message3 || "",
                message4: schedule.message4 || "",
                message5: schedule.message5 || "",
                mediaPath: schedule.mediaPath || null,
                mediaName: schedule.mediaName || null
              }))
            : []),
        companyId,
      };
    }
    
    if (initialValues && !initialValues.id) {
      // Si hay initialValues pero NO tiene id, es una copia
      // ✅ CORRECCIÓN: scheduledDate debe ser string "YYYY-MM-DD HH:mm:ss" en zona empresa
      let scheduledDateValue = "";
      if (initialValues.scheduledDate) {
        // Asegurar que sea string en formato correcto
        scheduledDateValue = String(initialValues.scheduledDate);
      }
      
      return {
        name: initialValues.name || "",
        whatsappId: initialValues.whatsappId || "",
        scheduledDate: scheduledDateValue,
        message1: initialValues.message1 || "",
        message2: initialValues.message2 || "",
        message3: initialValues.message3 || "",
        message4: initialValues.message4 || "",
        message5: initialValues.message5 || "",
        messageInterval: initialValues.messageInterval || 45,
        longerIntervalAfter: initialValues.longerIntervalAfter || 20,
        greaterInterval: initialValues.greaterInterval || 60,
        fileListId: initialValues.fileListId || "", // ✅ Lista de Archivos
        // ✅ CAMPOS DE RECURRENCIA
        // ✅ CORRECCIÓN CRÍTICA: Si campaignType es 'RECURRENTE' y no hay initialValues.isRecurring, establecer como true
        isRecurring: initialValues.isRecurring !== undefined ? initialValues.isRecurring : (campaignType === 'RECURRENTE'),
        recurrenceType: initialValues.recurrenceType || "",
        recurrenceInterval: initialValues.recurrenceInterval || "",
        recurrenceDaysOfWeek: initialValues.recurrenceDaysOfWeek || "",
        recurrenceDayOfMonth: initialValues.recurrenceDayOfMonth || "",
        recurrenceEndDate: initialValues.recurrenceEndDate || "",
        maxExecutions: initialValues.maxExecutions || "",
        // ✅ CAMPOS DE HORARIOS MÚLTIPLES (08/12/2025)
        useHourlySchedule: initialValues.useHourlySchedule || false,
        recurrenceHourlySchedules: initialValues.recurrenceHourlySchedules || [],
        companyId,
      };
    }
    
    // Estado inicial por defecto
    // ✅ Si es nueva campaña (sin campaignId y sin initialValues), calcular fecha inicial
    const calculateInitialScheduledDate = () => {
      if (!campaignId && !initialValues) {
        const timezone = getCampaignTimezoneSync();
        const nowInCompanyTimezone = moment().tz(timezone);
        let newDate = nowInCompanyTimezone.clone().add(30, 'minutes');
        
        // Validar que no sea en el pasado
        if (newDate.isBefore(nowInCompanyTimezone)) {
          newDate = nowInCompanyTimezone.clone().add(30, 'minutes');
        }
        
        // ✅ DateTimePickerWithTimezone espera formato "YYYY-MM-DD HH:mm:ss"
        return newDate.format('YYYY-MM-DD HH:mm:ss');
      }
      return "";
    };
    
    return {
      name: "",
      whatsappId: "",
      scheduledDate: calculateInitialScheduledDate(),
      message1: "",
      message2: "",
      message3: "",
      message4: "",
      message5: "",
      messageInterval: 45,
      longerIntervalAfter: 20,
      greaterInterval: 60,
      fileListId: "",
      // ✅ CAMPOS DE RECURRENCIA
      // ✅ CORRECCIÓN CRÍTICA: Si campaignType es 'RECURRENTE', establecer isRecurring como true
      isRecurring: campaignType === 'RECURRENTE',
      recurrenceType: "",
      recurrenceInterval: "",
      recurrenceDaysOfWeek: "",
      recurrenceDayOfMonth: "",
      recurrenceEndDate: "",
      maxExecutions: "",
      // ✅ CAMPOS DE HORARIOS MÚLTIPLES (08/12/2025)
      useHourlySchedule: false,
      recurrenceHourlySchedules: [],
      companyId,
    };
  }, [campaignId, initialValues, companyId, campaignType]); // ✅ Solo recalcular cuando estos valores cambien

  const handleClose = () => {
    setSelectedGroups([]);
    setAvailableGroups([]);
    setSearchGroupTerm("");
    setGroupPage(1);
    setMessageTab(0);
    setConfigTab(0); // ✅ Resetear pestaña principal a MENSAJE
    setSelectedConnection("all");
    setAttachment(null); // Reset archivo adjunto
    setCampaignMedia(null); // Reset media de campaña
    setHasInitialized(false); // Reset inicialización
    setIsCompleted(false); // ✅ Reset estado de completada
    // ✅ CORRECCIÓN CRÍTICA: Resetear formulario de Formik para limpiar todos los campos
    if (formikResetFormRef.current) {
      formikResetFormRef.current();
    }
    onClose();
  };

  // ✅ AUTO-CARGA: Cargar grupos automáticamente cuando cambia el filtro de conexión
  useEffect(() => {
    const loadGroups = async () => {
      if (!open || !selectedConnection) return;
      
      setLoadingGroups(true);
      // ✅ CORRECCIÓN: No resetear selectedGroups si estamos editando una campaña
      // Solo resetear si no hay campaignId (nueva campaña o copia)
      if (!campaignId) {
        setSelectedGroups([]); // Reset selecciones al cambiar filtro solo en nueva campaña
      }
      setSearchGroupTerm(""); // Reset búsqueda
      setGroupPage(1); // Reset página
      
      try {
        if (selectedConnection === "all") {
          // Cargar TODOS los grupos de todas las conexiones
          if (whatsapps && Array.isArray(whatsapps) && whatsapps.length > 0) {
            const allGroupsPromises = whatsapps.map(whatsapp => 
              getGroupsDb(whatsapp.id).then(res => res.data?.groups || [])
            );
            const allGroupsArrays = await Promise.all(allGroupsPromises);
            const allGroups = allGroupsArrays.flat();
            setAvailableGroups(allGroups);
          } else {
            setAvailableGroups([]);
          }
        } else {
          // Cargar solo grupos de la conexión seleccionada
          const response = await getGroupsDb(selectedConnection);
          setAvailableGroups(response.data?.groups || []);
        }
      } catch (err) {
        toastError(err);
        setAvailableGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };
    
    loadGroups();
  }, [open, selectedConnection, whatsapps, campaignId]);

  // ✅ CORRECCIÓN: Establecer selectedConnection cuando whatsapps se carga y hay initialValues
  useEffect(() => {
    if (initialValues && (initialValues.whatsappId || initialValues.whatsapp?.id) && whatsapps && whatsapps.length > 0) {
      const whatsappIdValue = (initialValues.whatsappId || initialValues.whatsapp.id).toString();
      const whatsappExists = whatsapps.some(w => w.id.toString() === whatsappIdValue);
      if (whatsappExists && selectedConnection === "all") {
        setSelectedConnection(whatsappIdValue);
      }
    }
  }, [whatsapps, initialValues, selectedConnection]);

  // ✅ NUEVO: Cargar datos cuando se copia una campaña (initialValues sin campaignId)
  useEffect(() => {
    if (!campaignId && initialValues && open && !hasInitialized) {
      
      // ✅ NOTA: selectedConnection se establece cuando se cargan los whatsapps (ver useEffect de loadWhatsapps)
      // no lo hacemos aquí porque whatsapps aún no está cargado
      
      // ✅ CORRECCIÓN DE BUG: Cargar grupos desde originalGroupIds (fuente de verdad) O shippings (fallback)
      // ✅ CRÍTICO: Para campañas recurrentes, shippings contiene TODOS los registros de todas las ejecuciones
      // Por eso debemos usar originalGroupIds como fuente de verdad, no shippings
      let uniqueGroupIds = [];
      
      if (initialValues.originalGroupIds) {
        try {
          uniqueGroupIds = JSON.parse(initialValues.originalGroupIds);
        } catch (err) {
          console.error("❌ [GroupCampaignModal] Error parseando originalGroupIds:", err);
        }
      } else if (initialValues.shippings && Array.isArray(initialValues.shippings)) {
        uniqueGroupIds = [...new Set(initialValues.shippings.map(s => s.groupId))];
        setCopiedGroupsData(initialValues.shippings);
      }
      
      if (uniqueGroupIds.length > 0) {
        setSelectedGroups(uniqueGroupIds);
      }
      
      // Si hay archivo en initialValues, cargar información del archivo
      if (initialValues.mediaPath && initialValues.mediaName) {
        setCampaignMedia({ path: initialValues.mediaPath, name: initialValues.mediaName });
      }
      
      // ✅ Si hay fileListId, también registrar
      if (initialValues.fileListId) {
      }
      
      setHasInitialized(true);
    }
  }, [campaignId, initialValues, open, hasInitialized, whatsapps]);


  // Cargar grupos de la campaña al editar (cuando hay campaignId)
  // ✅ CORRECCIÓN CRÍTICA: Solo cargar desde API si initialValues NO tiene todos los datos necesarios
  // Si handleEditCampaign ya cargó los datos completos, usar esos directamente
  useEffect(() => {
    if (campaignId && open && !hasInitialized) {
      // ✅ Verificar si initialValues ya tiene todos los datos necesarios (viene de handleEditCampaign)
      const hasCompleteData = initialValues && 
        initialValues.id && 
        (initialValues.message1 !== undefined || initialValues.message2 !== undefined || initialValues.message3 !== undefined) &&
        initialValues.scheduledDate !== undefined;
      
      if (hasCompleteData) {
        // ✅ initialValues ya tiene todos los datos, procesarlos directamente
        
        // ✅ Cargar whatsappId y establecer selectedConnection (solo si whatsapps ya está cargado)
        if (initialValues.whatsappId || initialValues.whatsapp?.id) {
          const whatsappIdValue = (initialValues.whatsappId || initialValues.whatsapp.id).toString();
          // ✅ CORRECCIÓN: Solo establecer selectedConnection si el whatsapp existe en la lista
          if (whatsapps && whatsapps.length > 0) {
            const whatsappExists = whatsapps.some(w => w.id.toString() === whatsappIdValue);
            if (whatsappExists) {
              setSelectedConnection(whatsappIdValue);
            }
          }
        }
        
        // ✅ CORRECCIÓN DE BUG: Obtener grupos desde originalGroupIds (fuente de verdad) O groupIds O shippings (fallback)
        // ✅ CRÍTICO: Para campañas recurrentes, shippings contiene TODOS los registros de todas las ejecuciones
        // Por eso debemos usar originalGroupIds como fuente de verdad, no shippings ni groupIds si vienen de shippings
        let uniqueGroupIds = [];
        
        // ✅ CORRECCIÓN: Prioridad 1: originalGroupIds (fuente de verdad para grupos únicos)
        if (initialValues.originalGroupIds) {
          try {
            uniqueGroupIds = JSON.parse(initialValues.originalGroupIds);
          } catch (err) {
            console.error("❌ [GroupCampaignModal] Error parseando originalGroupIds:", err);
          }
        }
        // Prioridad 2: groupIds (viene de handleEditCampaign, pero solo si no hay originalGroupIds)
        // ✅ NOTA: handleEditCampaign ahora también usa originalGroupIds primero, pero por seguridad verificamos aquí también
        else if (initialValues.groupIds && Array.isArray(initialValues.groupIds) && initialValues.groupIds.length > 0) {
          // ✅ CORRECCIÓN: Eliminar duplicados por si acaso groupIds viene de shippings
          uniqueGroupIds = [...new Set(initialValues.groupIds)];
        }
        // Prioridad 3: shippings (solo como fallback si no hay originalGroupIds ni groupIds)
        else if (initialValues.shippings && Array.isArray(initialValues.shippings) && initialValues.shippings.length > 0) {
          uniqueGroupIds = [...new Set(initialValues.shippings.map(s => s.groupId || s.groupDetails?.groupId).filter(id => id))];
        }
        
        if (uniqueGroupIds.length > 0) {
          setSelectedGroups(uniqueGroupIds);
        }
        
        // Cargar información del archivo si existe
        if (initialValues.mediaPath && initialValues.mediaName) {
          setCampaignMedia({ path: initialValues.mediaPath, name: initialValues.mediaName });
        }
        
        // ✅ Verificar si la campaña ya fue completada
        if (initialValues.endDate || initialValues.status === 'FINALIZADA') {
          setIsCompleted(true);
          console.log("⚠️ [GroupCampaignModal] Campaña ya completada (solo lectura)");
        }
        
        // ✅ Mapear horarios desde hourlySchedules a recurrenceHourlySchedules
        if (initialValues.useHourlySchedule && initialValues.hourlySchedules && Array.isArray(initialValues.hourlySchedules)) {
          const mappedSchedules = initialValues.hourlySchedules.map(schedule => ({
            time: schedule.time,
            message1: schedule.message1 || "",
            message2: schedule.message2 || "",
            message3: schedule.message3 || "",
            message4: schedule.message4 || "",
            message5: schedule.message5 || "",
            mediaPath: schedule.mediaPath || null,
            mediaName: schedule.mediaName || null
          }));
          // ✅ CRÍTICO: Actualizar initialValues con los horarios mapeados
          if (initialValues) {
            initialValues.recurrenceHourlySchedules = mappedSchedules;
          }
        }
        
        setHasInitialized(true);
        return; // ✅ No hacer llamada a API, ya tenemos todos los datos
      }
      
      // ✅ Si initialValues NO tiene todos los datos, cargar desde API
      console.log("📋 [GroupCampaignModal] Cargando datos desde API...");
      getGroupCampaign(campaignId)
        .then(({ data }) => {
          // ✅ CORRECCIÓN: scheduledDate debe ser string "YYYY-MM-DD HH:mm:ss" en zona empresa
          if (data.scheduledDate) {
            data.scheduledDate = String(data.scheduledDate);
          }
          if (data.nextScheduledAt) {
            data.nextScheduledAt = String(data.nextScheduledAt);
          }
          
          // ✅ CORRECCIÓN: Actualizar initialValues con scheduledDate como string
          if (initialValues) {
            initialValues.scheduledDate = data.scheduledDate || "";
            initialValues.nextScheduledAt = data.nextScheduledAt || "";
          }
          
          // ✅ CORRECCIÓN: Cargar whatsappId y establecer selectedConnection (solo si whatsapps ya está cargado)
          if (data.whatsappId) {
            const whatsappIdValue = data.whatsappId.toString();
            // ✅ CORRECCIÓN: Solo establecer selectedConnection si el whatsapp existe en la lista
            if (whatsapps && whatsapps.length > 0) {
              const whatsappExists = whatsapps.some(w => w.id.toString() === whatsappIdValue);
              if (whatsappExists) {
                setSelectedConnection(whatsappIdValue);
              }
            }
          }
          
          // ✅ CORRECCIÓN DE BUG: Obtener grupos desde originalGroupIds (fuente de verdad) O shippings (fallback)
          // ✅ CRÍTICO: Para campañas recurrentes, shippings contiene TODOS los registros de todas las ejecuciones
          // Por eso debemos usar originalGroupIds como fuente de verdad, no shippings
          let uniqueGroupIds = [];
          
          // Prioridad 1: originalGroupIds (fuente de verdad para grupos únicos)
          if (data.originalGroupIds) {
            try {
              uniqueGroupIds = JSON.parse(data.originalGroupIds);
            } catch (err) {
              console.error("❌ [GroupCampaignModal] Error parseando originalGroupIds:", err);
            }
          }
          // Prioridad 2: shippings (solo como fallback si no hay originalGroupIds)
          else if (data.shippings && Array.isArray(data.shippings) && data.shippings.length > 0) {
            uniqueGroupIds = [...new Set(data.shippings.map(s => s.groupId))];
          }
          
          if (uniqueGroupIds.length > 0) {
            setSelectedGroups(uniqueGroupIds);
          }
          
          // Cargar información del archivo si existe
          if (data.mediaPath && data.mediaName) {
            setCampaignMedia({ path: data.mediaPath, name: data.mediaName });
          }
          
          // ✅ Verificar si la campaña ya fue completada (tiene endDate)
          if (data.endDate) {
            setIsCompleted(true);
          }
          
          // ✅ NUEVO (08/12/2025): Mapear horarios desde hourlySchedules a recurrenceHourlySchedules
          if (data.useHourlySchedule && data.hourlySchedules && Array.isArray(data.hourlySchedules) && data.hourlySchedules.length > 0) {
            const mappedSchedules = data.hourlySchedules.map(schedule => ({
              time: schedule.time,
              message1: schedule.message1 || "",
              message2: schedule.message2 || "",
              message3: schedule.message3 || "",
              message4: schedule.message4 || "",
              message5: schedule.message5 || "",
              mediaPath: schedule.mediaPath || null,
              mediaName: schedule.mediaName || null
            }));
            // ✅ CRÍTICO: Actualizar initialValues con los horarios mapeados
            if (initialValues) {
              initialValues.recurrenceHourlySchedules = mappedSchedules;
              initialValues.useHourlySchedule = true; // Asegurar que esté activado
            }
          }
          
          setHasInitialized(true);
        })
        .catch(err => {
          console.error("Error cargando campaña:", err);
          toastError(err);
        });
    }
  }, [campaignId, initialValues, open, hasInitialized, whatsapps]);
  
  // ✅ NUEVO: Sincronizar selectedGroups cuando availableGroups se carga y estamos editando
  useEffect(() => {
    if (campaignId && hasInitialized && availableGroups.length > 0 && selectedGroups.length === 0) {
      // Si estamos editando y tenemos grupos disponibles pero no hay grupos seleccionados,
      // intentar cargar desde la campaña nuevamente
      getGroupCampaign(campaignId)
        .then(({ data }) => {
          // ✅ CORRECCIÓN DE BUG: Usar originalGroupIds como fuente de verdad, no shippings
          let uniqueGroupIds = [];
          
          if (data.originalGroupIds) {
            try {
              uniqueGroupIds = JSON.parse(data.originalGroupIds);
            } catch (err) {
              console.error("❌ [GroupCampaignModal] Error parseando originalGroupIds:", err);
            }
          } else if (data.shippings && Array.isArray(data.shippings) && data.shippings.length > 0) {
            uniqueGroupIds = [...new Set(data.shippings.map(s => s.groupId))];
          }
          
          if (uniqueGroupIds.length > 0) {
            setSelectedGroups(uniqueGroupIds);
          }
        })
        .catch(err => {
          console.error("Error sincronizando grupos:", err);
        });
    }
  }, [campaignId, hasInitialized, availableGroups.length, selectedGroups.length]);

  // ✅ CORRECCIÓN: La lógica de recurrenceInterval se maneja en el render de Formik
  // pero usando el ref para evitar actualizaciones durante el render inicial

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // ✅ ELIMINAR DUPLICADOS de availableGroups antes de filtrar
  const uniqueGroups = Array.from(
    new Map(availableGroups.map(group => [group.groupId, group])).values()
  );

  const filteredGroups = uniqueGroups.filter(
    (group) =>
      group.name?.toLowerCase().includes(searchGroupTerm.toLowerCase()) ||
      group.groupId?.includes(searchGroupTerm)
  );

  const totalPages = Math.ceil(filteredGroups.length / groupsPerPage);
  const startIndex = (groupPage - 1) * groupsPerPage;
  const endIndex = startIndex + groupsPerPage;
  const paginatedGroups = filteredGroups.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (groupPage > 1) setGroupPage(groupPage - 1);
  };

  const handleNextPage = () => {
    if (groupPage < totalPages) setGroupPage(groupPage + 1);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAttachment(file);
      toast.success(`Archivo seleccionado: ${file.name}`);
    }
  };

  // ✅ NUEVO (09/12/2025): Función para formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // ✅ Nueva función: Manejar adjuntar o eliminar archivo
  const handleAttachmentClick = () => {
    if (attachment || campaignMedia) {
      // Hay archivo adjuntado → ELIMINAR
      setAttachment(null);
      setCampaignMedia(null);
      toast.info("Archivo eliminado");
    } else {
      // No hay archivo → ADJUNTAR
      if (attachmentFile.current) {
        attachmentFile.current.click();
      }
    }
  };

  // ✅ NUEVO (09/12/2025): Handler para cambio de Tipo de Recurrencia
  const handleRecurrenceTypeChange = (newValue, currentValue, setFieldValue, values) => {
    // Si cambia de "diaria" a otro tipo y hay horarios configurados
    if (currentValue === "diaria" && 
        newValue !== "diaria" && 
        values.useHourlySchedule && 
        values.recurrenceHourlySchedules && 
        values.recurrenceHourlySchedules.length > 0) {
      // Guardar cambio pendiente y mostrar modal
      setPendingRecurrenceTypeChange(newValue);
      setPendingOldRecurrenceType("diaria");
      setPendingNewRecurrenceType(newValue);
      setPendingSchedulesCount(values.recurrenceHourlySchedules.length);
      setConfirmChangeRecurrenceTypeModalOpen(true);
    } else {
      // Aplicar cambio directamente
      setFieldValue('recurrenceType', newValue);
      // ✅ NUEVO (09/12/2025): Si cambia a "diaria", establecer automáticamente recurrenceInterval = 1
      if (newValue === "diaria") {
        if (!values.recurrenceInterval || values.recurrenceInterval === "" || values.recurrenceInterval < 1) {
          setFieldValue('recurrenceInterval', 1);
        }
      }
      // Si cambia de "diaria" a otro tipo, desactivar useHourlySchedule automáticamente
      if (currentValue === "diaria" && newValue !== "diaria") {
        setFieldValue('useHourlySchedule', false);
        setFieldValue('recurrenceHourlySchedules', []);
      }
    }
  };

  // ✅ ELIMINADO: handleDisableRecurrence - No se usa (el switch "Habilitar Recurrencia" fue eliminado)

  // ✅ NUEVO (09/12/2025): Confirmar cambio de Tipo de Recurrencia
  const handleConfirmChangeRecurrenceType = (setFieldValue) => {
    if (pendingRecurrenceTypeChange !== null) {
      setFieldValue('recurrenceType', pendingRecurrenceTypeChange);
      // Desactivar useHourlySchedule automáticamente
      setFieldValue('useHourlySchedule', false);
      setFieldValue('recurrenceHourlySchedules', []);
      toast.success(`Tipo de recurrencia cambiado a "${pendingRecurrenceTypeChange}". Los horarios subsecuentes han sido eliminados.`);
    }
    setConfirmChangeRecurrenceTypeModalOpen(false);
    setPendingRecurrenceTypeChange(null);
    setPendingOldRecurrenceType(null);
    setPendingNewRecurrenceType(null);
  };

  // ✅ NUEVO (09/12/2025): Confirmar deshabilitar Recurrencia
  const handleConfirmDisableRecurrence = (setFieldValue) => {
    setFieldValue('isRecurring', false);
    // Limpiar todos los campos de recurrencia
    setFieldValue('recurrenceType', '');
    setFieldValue('recurrenceInterval', '');
    setFieldValue('recurrenceDaysOfWeek', '');
    setFieldValue('recurrenceDayOfMonth', '');
    setFieldValue('recurrenceEndDate', '');
    setFieldValue('maxExecutions', '');
    setFieldValue('useHourlySchedule', false);
    setFieldValue('recurrenceHourlySchedules', []);
    toast.success("Recurrencia desactivada. Todos los horarios subsecuentes han sido eliminados.");
    setConfirmDisableRecurrenceModalOpen(false);
  };

  const handleConfirmAndSave = async () => {
    if (!pendingSubmit) return;
    
    setConfirmModalOpen(false);
    
    // Continuar con el proceso de guardado
    const { values, setSubmitting } = pendingSubmit;
    setPendingSubmit(null);
    
    await processSave(values, setSubmitting);
  };

  const handleSave = async (values, setSubmitting) => {
    // ✅ Alerta si no hay fecha programada
    if (!values.scheduledDate) {
      setPendingSubmit({ values, setSubmitting });
      setConfirmModalOpen(true);
      return;
    }
    
    await processSave(values, setSubmitting);
  };

  // ✅ Función para validación manual (similar a CampaignModal)
  const validateForm = (values) => {
    const errors = {};
    
    // Validar nombre
    if (!values.name || values.name.trim() === "") {
      errors.name = "El nombre de la campaña es obligatorio";
    } else if (values.name.trim().length < 3) {
      errors.name = "El nombre debe tener al menos 3 caracteres";
    } else if (values.name.trim().length > 40) {
      errors.name = "El nombre no puede exceder 40 caracteres";
    }
    
    // Validar conexión (whatsappId)
    // ✅ Usar selectedConnection del estado si values.whatsappId no está disponible
    const connectionToValidate = values.whatsappId || selectedConnection;
    if (!connectionToValidate || connectionToValidate === "" || connectionToValidate === "all") {
      errors.whatsappId = "La conexión es obligatoria";
    }
    
    // Validar al menos un grupo seleccionado
    // ✅ Usar values.selectedGroups si existe, sino usar el estado selectedGroups
    const groupsToValidate = values.selectedGroups || selectedGroups || [];
    if (groupsToValidate.length === 0) {
      errors.selectedGroups = "Debe seleccionar al menos un grupo";
    }
    
    // Validar fecha programada
    if (!values.scheduledDate || values.scheduledDate === "") {
      errors.scheduledDate = "La fecha programada es obligatoria";
    }
    
    // Validar al menos un mensaje, archivo adjunto o lista de archivos
    const hasAttachment = attachment || campaignMedia;
    const hasFileList = values.fileListId && values.fileListId !== "";
    const hasMessages = (values.message1 && values.message1.trim() !== "") || 
                       (values.message2 && values.message2.trim() !== "") || 
                       (values.message3 && values.message3.trim() !== "") || 
                       (values.message4 && values.message4.trim() !== "") || 
                       (values.message5 && values.message5.trim() !== "");
    
    if (!hasAttachment && !hasFileList && !hasMessages) {
      // Mostrar error en el primer campo de mensaje
      errors.message1 = "Debe incluir al menos un mensaje, un archivo adjunto O una lista de archivos";
    }
    
    // Validar intervalos
    if (!values.messageInterval || values.messageInterval < 5) {
      errors.messageInterval = "El intervalo entre mensajes debe ser al menos 5 segundos";
    }
    if (!values.longerIntervalAfter || values.longerIntervalAfter < 1) {
      errors.longerIntervalAfter = "Debe especificar después de cuántos grupos aplicar intervalo mayor";
    }
    if (!values.greaterInterval || values.greaterInterval < 10) {
      errors.greaterInterval = "El intervalo mayor debe ser al menos 10 segundos";
    }
    
    // ✅ VALIDACIONES DE RECURRENCIA - Usar currentCampaignType en lugar de values.isRecurring
    // Nota: currentCampaignType se determina dinámicamente, pero en validateForm no tenemos acceso directo
    // Por lo tanto, verificamos si hay campos de recurrencia configurados
    const isRecurring = currentCampaignType === 'RECURRENTE' || 
                       (values.recurrenceType && values.recurrenceType !== "") ||
                       (values.recurrenceInterval && values.recurrenceInterval !== "");
    
    if (isRecurring || currentCampaignType === 'RECURRENTE') {
      if (!values.recurrenceType || values.recurrenceType === "") {
        errors.recurrenceType = "El tipo de recurrencia es obligatorio";
      }
      
      // Para recurrencia diaria con horarios múltiples, el intervalo se valida diferente
      if (values.recurrenceType === "diaria" && values.useHourlySchedule) {
        // Si usa horarios múltiples, el intervalo debe ser 1 (ya se establece automáticamente)
        if (!values.recurrenceInterval || values.recurrenceInterval < 1) {
          errors.recurrenceInterval = "El intervalo de recurrencia debe ser al menos 1";
        }
      } else if (values.recurrenceType && values.recurrenceType !== "diaria") {
        // Para otros tipos de recurrencia, validar intervalo normalmente
        if (!values.recurrenceInterval || values.recurrenceInterval === "" || values.recurrenceInterval < 1) {
          errors.recurrenceInterval = "El intervalo de recurrencia es obligatorio y debe ser mayor a 0";
        }
      }
      
      if (values.recurrenceType === "semanal") {
        if (!values.recurrenceDaysOfWeek || values.recurrenceDaysOfWeek === "" || JSON.parse(values.recurrenceDaysOfWeek || "[]").length === 0) {
          errors.recurrenceDaysOfWeek = "Debe seleccionar al menos un día de la semana";
        }
      }
      
      if (values.recurrenceType === "mensual") {
        if (!values.recurrenceDayOfMonth || values.recurrenceDayOfMonth < 1 || values.recurrenceDayOfMonth > 31) {
          errors.recurrenceDayOfMonth = "Debe especificar un día del mes válido (1-31)";
        }
      }
      
      // Validaciones específicas para horarios múltiples
      if (values.useHourlySchedule && values.recurrenceType === "diaria") {
        const hasMasterMessages = 
          (values.message1 && values.message1.trim() !== "") ||
          (values.message2 && values.message2.trim() !== "") ||
          (values.message3 && values.message3.trim() !== "") ||
          (values.message4 && values.message4.trim() !== "") ||
          (values.message5 && values.message5.trim() !== "") ||
          (values.fileListId !== null && values.fileListId !== undefined);
        
        if (!hasMasterMessages) {
          errors.message1 = "Los mensajes maestros son obligatorios cuando 'Enviar por horas múltiples' está activado";
        }
        
        if (!values.recurrenceHourlySchedules || values.recurrenceHourlySchedules.length === 0) {
          errors.recurrenceHourlySchedules = "Debe configurar al menos un horario cuando la opción 'Enviar por horas múltiples' está activada";
        }
      }
    }
    
    return errors;
  };

  const processSave = async (values, setSubmitting, shouldSkipDateCheck = false, shouldSkipShortTimeCheck = false, setFieldValue = null) => {
    try {
      // ✅ FASE 2: Validar fecha programada en el pasado o < 10 minutos
      if (!shouldSkipDateCheck && values.scheduledDate && values.scheduledDate !== "" && values.scheduledDate !== null) {
        const timezone = getCampaignTimezoneSync();
        
        // Parsear scheduledDate en zona empresa
        let scheduledMoment;
        if (values.scheduledDate.includes('T')) {
          scheduledMoment = moment.tz(values.scheduledDate, timezone);
        } else {
          scheduledMoment = moment.tz(values.scheduledDate, 'YYYY-MM-DD HH:mm:ss', timezone);
        }
        
        if (scheduledMoment.isValid()) {
          const nowInCompanyTimezone = moment().tz(timezone);
          const diffMinutes = scheduledMoment.diff(nowInCompanyTimezone, 'minutes', true);
          
          if (scheduledMoment.isBefore(nowInCompanyTimezone)) {
            // Fecha pasada
            const pastDiffMinutes = nowInCompanyTimezone.diff(scheduledMoment, 'minutes');
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setDatePastMessage(
              `La fecha programada (${formattedDate}) está en el pasado (hace ${Math.ceil(pastDiffMinutes)} minutos).\n\n¿Desea actualizar la fecha a ahora mismo (hora actual + 2 minutos) para enviar la campaña inmediatamente?`
            );
            setPendingSaveValues(values);
            setPendingSetFieldValue(setFieldValue);
            saveCampaignRef.current = (vals, skipDate, skipShort) => processSave(vals, setSubmitting, skipDate, skipShort, setFieldValue);
            setDatePastModalOpen(true);
            setSubmitting(false);
            return;
          } else if (diffMinutes > 0 && diffMinutes < 10 && !shouldSkipShortTimeCheck) {
            // Fecha futura pero < 10 minutos
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setShortTimeMessage(
              `La fecha programada (${formattedDate}) está a menos de 10 minutos (${Math.ceil(diffMinutes)} minutos). Si programa con menos de 10 minutos, no tendrá opción a modificar esta campaña ya que los tiempos son muy cortos para su procesamiento. Se sugiere programar envíos mayores a 30 minutos.`
            );
            setPendingSaveValues(values);
            setPendingScheduledAt(values.scheduledDate);
            saveCampaignRef.current = (vals, skipDate, skipShort) => processSave(vals, setSubmitting, skipDate, skipShort, setFieldValue);
            setShortTimeModalOpen(true);
            setSubmitting(false);
            return;
          }
        }
      }

      // ✅ VALIDACIÓN 1: Nombre obligatorio
      if (!values.name || values.name.trim() === "") {
        toast.error("El nombre de la campaña es obligatorio");
        setSubmitting(false);
        return;
      }
      
      // ✅ VALIDACIÓN 2: Mínimo 1 grupo seleccionado
      if (selectedGroups.length === 0) {
        toast.error("Debe seleccionar al menos un grupo");
        setSubmitting(false);
        return;
      }
      
      // ✅ VALIDACIÓN 3: Archivo, Lista de Archivos o mensaje (al menos uno)
      const hasAttachment = attachment || campaignMedia;
      const hasFileList = values.fileListId && values.fileListId !== ""; // ✅ Nueva validación
      const hasMessages = values.message1 || values.message2 || values.message3 || values.message4 || values.message5;
      
      // ✅ Si NINGUNA de las 3 opciones tiene contenido → ERROR
      if (!hasAttachment && !hasFileList && !hasMessages) {
        toast.error("Debe incluir al menos un mensaje, un archivo adjunto O una lista de archivos");
        setSubmitting(false);
        return;
      }
      
      // ✅ VALIDACIÓN 4: Intervalos obligatorios
      if (!values.messageInterval || values.messageInterval < 5) {
        toast.error("El intervalo entre mensajes debe ser al menos 5 segundos");
        setSubmitting(false);
        return;
      }
      if (!values.longerIntervalAfter || values.longerIntervalAfter < 1) {
        toast.error("Debe especificar después de cuántos grupos aplicar intervalo mayor");
        setSubmitting(false);
        return;
      }
      if (!values.greaterInterval || values.greaterInterval < 10) {
        toast.error("El intervalo mayor debe ser al menos 10 segundos");
        setSubmitting(false);
        return;
      }
      
      // ✅ VALIDACIÓN 5: Validaciones de recurrencia
      if (values.isRecurring) {
        if (!values.recurrenceType || values.recurrenceType === "") {
          toast.error("Debe seleccionar un tipo de recurrencia");
          setSubmitting(false);
          return;
        }
        if (!values.recurrenceInterval || values.recurrenceInterval < 1) {
          toast.error("El intervalo de recurrencia debe ser al menos 1");
          setSubmitting(false);
          return;
        }
        if (values.recurrenceType === "semanal") {
          if (!values.recurrenceDaysOfWeek || values.recurrenceDaysOfWeek === "" || JSON.parse(values.recurrenceDaysOfWeek).length === 0) {
            toast.error("Debe seleccionar al menos un día de la semana");
            setSubmitting(false);
            return;
          }
        }
        if (values.recurrenceType === "mensual") {
          if (!values.recurrenceDayOfMonth || values.recurrenceDayOfMonth < 1 || values.recurrenceDayOfMonth > 31) {
            toast.error("Debe especificar un día del mes válido (1-31)");
            setSubmitting(false);
            return;
          }
        }
        // ✅ NUEVO (08/12/2025): Validación para horarios múltiples
        if (values.useHourlySchedule && values.recurrenceType === "diaria") {
          // ✅ NUEVO (09/12/2025): Validar mensajes maestros obligatorios
          const hasMasterMessages = 
            (values.message1 && values.message1.trim() !== "") ||
            (values.message2 && values.message2.trim() !== "") ||
            (values.message3 && values.message3.trim() !== "") ||
            (values.message4 && values.message4.trim() !== "") ||
            (values.message5 && values.message5.trim() !== "") ||
            (values.fileListId !== null && values.fileListId !== undefined);
          
          if (!hasMasterMessages) {
            toast.error(
              "Los mensajes maestros son obligatorios cuando 'Enviar por horas múltiples' está activado. " +
              "Estos mensajes se usan en la primera ejecución del día (hora programada principal). " +
              "Debe configurar al menos un mensaje (MSG 1-5) o una lista de archivos."
            );
            setSubmitting(false);
            return;
          }
          
          if (!values.recurrenceHourlySchedules || values.recurrenceHourlySchedules.length === 0) {
            toast.error("Debe configurar al menos un horario cuando la opción 'Enviar por horas múltiples' está activada");
            setSubmitting(false);
            return;
          }
          // Validar que cada horario tenga al menos message1
          const invalidSchedules = values.recurrenceHourlySchedules.filter(s => !s.time || !s.message1 || s.message1.trim() === "");
          if (invalidSchedules.length > 0) {
            toast.error("Todos los horarios deben tener una hora y al menos el Mensaje 1 configurado");
            setSubmitting(false);
            return;
          }
          // Validar que no haya horarios duplicados
          const times = values.recurrenceHourlySchedules.map(s => s.time);
          const uniqueTimes = [...new Set(times)];
          if (times.length !== uniqueTimes.length) {
            toast.error("No se pueden tener horarios duplicados");
            setSubmitting(false);
            return;
          }
          
          // ✅ NUEVO (09/12/2025): Validar conflictos con scheduledDate
          if (values.scheduledDate) {
            try {
              const scheduledDateObj = new Date(values.scheduledDate);
              const scheduledHour = String(scheduledDateObj.getHours()).padStart(2, '0') + ':' + String(scheduledDateObj.getMinutes()).padStart(2, '0');
              
              // Validar que ningún horario coincida con scheduledDate
              const conflictSchedule = values.recurrenceHourlySchedules.find(s => s.time === scheduledHour);
              if (conflictSchedule) {
                toast.error(
                  `No puede configurar un horario que coincida con la hora programada principal (${scheduledHour}). ` +
                  `La hora principal siempre usa los mensajes maestros. Por favor, cambie el horario configurado.`
                );
                setSubmitting(false);
                return;
              }
              
              // Validar que todos los horarios sean posteriores a scheduledDate
              const invalidSchedulesBefore = values.recurrenceHourlySchedules.filter(s => s.time <= scheduledHour);
              if (invalidSchedulesBefore.length > 0) {
                toast.error(
                  `Los horarios no pueden ser anteriores o iguales a la hora programada principal (${scheduledHour}). ` +
                  `La hora principal siempre se ejecuta primero. Por favor, configure horarios posteriores a ${scheduledHour}.`
                );
                setSubmitting(false);
                return;
              }
            } catch (error) {
              console.warn("Error validando conflictos con scheduledDate:", error);
            }
          }
        }
        // Si es recurrente, scheduledDate es obligatorio
        if (!values.scheduledDate) {
          toast.error("Las campañas recurrentes deben tener una fecha programada");
          setSubmitting(false);
          return;
        }
      }

      console.log("📤 [GroupCampaignModal] Valores del formulario:", values);
      console.log("📤 [GroupCampaignModal] scheduledDate capturado:", values.scheduledDate);
      console.log("📤 [GroupCampaignModal] Archivo adjunto:", attachment);

      // ✅ FASE 2: Validar fecha programada en el pasado o < 10 minutos
      if (!shouldSkipDateCheck && values.scheduledDate && values.scheduledDate !== "" && values.scheduledDate !== null) {
        const timezone = getCampaignTimezoneSync();
        
        // Parsear scheduledDate en zona empresa (viene como string "YYYY-MM-DDTHH:mm" o similar)
        let scheduledMoment;
        if (values.scheduledDate.includes('T')) {
          // Formato ISO (datetime-local)
          scheduledMoment = moment.tz(values.scheduledDate, timezone);
        } else {
          // Formato "YYYY-MM-DD HH:mm:ss"
          scheduledMoment = moment.tz(values.scheduledDate, 'YYYY-MM-DD HH:mm:ss', timezone);
        }
        
        if (scheduledMoment.isValid()) {
          const nowInCompanyTimezone = moment().tz(timezone);
          const diffMinutes = scheduledMoment.diff(nowInCompanyTimezone, 'minutes', true);
          
          if (scheduledMoment.isBefore(nowInCompanyTimezone)) {
            // Fecha pasada
            const pastDiffMinutes = nowInCompanyTimezone.diff(scheduledMoment, 'minutes');
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setDatePastMessage(
              `La fecha programada (${formattedDate}) está en el pasado (hace ${Math.ceil(pastDiffMinutes)} minutos).\n\n¿Desea actualizar la fecha a ahora mismo (hora actual + 2 minutos) para enviar la campaña inmediatamente?`
            );
            setPendingSaveValues(values);
            setPendingSetFieldValue(setFieldValue);
            saveCampaignRef.current = (vals, skipDate, skipShort) => processSave(vals, setSubmitting, skipDate, skipShort, setFieldValue);
            setDatePastModalOpen(true);
            setSubmitting(false);
            return;
          } else if (diffMinutes > 0 && diffMinutes < 10 && !shouldSkipShortTimeCheck) {
            // Fecha futura pero < 10 minutos
            const formattedDate = scheduledMoment.format('DD/MM/YYYY HH:mm');
            setShortTimeMessage(
              `La fecha programada (${formattedDate}) está a menos de 10 minutos (${Math.ceil(diffMinutes)} minutos). Si programa con menos de 10 minutos, no tendrá opción a modificar esta campaña ya que los tiempos son muy cortos para su procesamiento. Se sugiere programar envíos mayores a 30 minutos.`
            );
            setPendingSaveValues(values);
            setPendingScheduledAt(values.scheduledDate);
            saveCampaignRef.current = (vals, skipDate, skipShort) => processSave(vals, setSubmitting, skipDate, skipShort, setFieldValue);
            setShortTimeModalOpen(true);
            setSubmitting(false);
            return;
          }
        }
      }

      const campaignData = {
        name: values.name,
        scheduledDate: values.scheduledDate || null,
        groupIds: selectedGroups,
        message1: values.message1 || "",
        message2: values.message2 || "",
        message3: values.message3 || "",
        message4: values.message4 || "",
        message5: values.message5 || "",
        messageInterval: values.messageInterval || 20,
        longerIntervalAfter: values.longerIntervalAfter || 20,
        greaterInterval: values.greaterInterval || 60,
        fileListId: values.fileListId || null, // ✅ Lista de Archivos
        // ✅ CAMPOS DE RECURRENCIA
        isRecurring: values.isRecurring || false,
        recurrenceType: values.recurrenceType || null,
        recurrenceInterval: values.recurrenceInterval || null,
        recurrenceDaysOfWeek: values.recurrenceDaysOfWeek || null,
        recurrenceDayOfMonth: values.recurrenceDayOfMonth || null,
        recurrenceEndDate: values.recurrenceEndDate || null,
        maxExecutions: values.maxExecutions || null,
        // ✅ CAMPOS DE HORARIOS MÚLTIPLES (08/12/2025)
        useHourlySchedule: values.useHourlySchedule || false,
        recurrenceHourlySchedules: values.recurrenceHourlySchedules || [],
      };

      console.log(`🔍 [GroupCampaignModal] Valores del formulario - isRecurring: ${values.isRecurring} (tipo: ${typeof values.isRecurring})`);
      
      // ✅ NUEVO (10/01/2026): Subir archivos de horarios múltiples antes de guardar la campaña
      if (values.useHourlySchedule && values.recurrenceHourlySchedules && values.recurrenceHourlySchedules.length > 0) {
        console.log("📤 [GroupCampaignModal] Subiendo archivos de horarios múltiples...");
        
        for (let i = 0; i < values.recurrenceHourlySchedules.length; i++) {
          const schedule = values.recurrenceHourlySchedules[i];
          
          // Si tiene mediaFile pero no mediaPath, subir el archivo
          if (schedule.mediaFile && !schedule.mediaPath) {
            try {
              console.log(`📤 [GroupCampaignModal] Subiendo archivo para horario ${schedule.time}: ${schedule.mediaName}`);
              
              const formData = new FormData();
              formData.append("file", schedule.mediaFile);
              
              const uploadResponse = await uploadSingleFile(formData);
              
              // Actualizar el horario con el mediaPath devuelto
              schedule.mediaPath = uploadResponse.data.path;
              schedule.mediaName = uploadResponse.data.name;
              
              // Eliminar mediaFile ya que ya se subió
              delete schedule.mediaFile;
              
              console.log(`✅ [GroupCampaignModal] Archivo subido para horario ${schedule.time}: ${uploadResponse.data.path}`);
            } catch (uploadError) {
              console.error(`❌ [GroupCampaignModal] Error subiendo archivo para horario ${schedule.time}:`, uploadError);
              toast.error(`Error subiendo archivo para horario ${schedule.time}. Por favor, intente nuevamente.`);
              setSubmitting(false);
              return;
            }
          }
        }
        
        // Actualizar campaignData con los horarios actualizados (con mediaPath)
        campaignData.recurrenceHourlySchedules = values.recurrenceHourlySchedules.map(s => ({
          time: s.time,
          message1: s.message1 || "",
          message2: s.message2 || "",
          message3: s.message3 || "",
          message4: s.message4 || "",
          message5: s.message5 || "",
          mediaPath: s.mediaPath || null,
          mediaName: s.mediaName || null
        }));
        
        console.log("✅ [GroupCampaignModal] Todos los archivos de horarios subidos correctamente");
      }
      
      console.log("📤 [GroupCampaignModal] Enviando datos a backend:", campaignData);
      
      let response;
      if (campaignId) {
        console.log("✏️ [GroupCampaignModal] Actualizando campaña ID:", campaignId);
        response = await updateGroupCampaign(campaignId, campaignData);
        
        // Si hay archivo adjunto, enviarlo
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await uploadGroupCampaignMedia(campaignId, formData);
          console.log("📎 [GroupCampaignModal] Archivo adjuntado a campaña");
        }
        
        console.log("✅ [GroupCampaignModal] Respuesta actualización:", response.data);
        toast.success("Campaña actualizada exitosamente");
      } else {
        console.log("➕ [GroupCampaignModal] Creando nueva campaña");
        
        // ✅ CAMBIO CRÍTICO: Para envíos inmediatos sin fecha, NO crear la campaña aún
        // En su lugar, crear la campaña con scheduledDate futuro para que el backend no la procese inmediatamente
        // Luego subir el archivo, y FINALMENTE actualizar scheduledDate a null para que se envíe
        
        if (attachment && !campaignData.scheduledDate) {
          // Si hay archivo y no hay fecha, agregar 5 segundos para dar tiempo de subir archivo
          const futureDate = new Date(Date.now() + 5000);
          campaignData.scheduledDate = futureDate.toISOString();
        }
        
        response = await createGroupCampaign(campaignData);
        console.log("✅ [GroupCampaignModal] Respuesta creación:", response.data);
        console.log("📊 [GroupCampaignModal] Estado de campaña:", response.data?.status);
        
        // Si hay archivo adjunto, enviarlo DESPUÉS de crear la campaña
        if (attachment && response.data?.id) {
          const formData = new FormData();
          formData.append("file", attachment);
          await uploadGroupCampaignMedia(response.data.id, formData);
          console.log("📎 [GroupCampaignModal] Archivo adjuntado a campaña nueva");
          
          // Si era un envío inmediato sin fecha original, actualizar para que se envíe ahora
          if (!values.scheduledDate) {
            // Actualizar la campaña para quitar la fecha programada y que se envíe inmediatamente
            await updateGroupCampaign(response.data.id, {
              scheduledDate: null
            });
            console.log("⏰ [GroupCampaignModal] Campaña actualizada para envío inmediato después de subir archivo");
          }
        }
        
        toast.success("Campaña creada exitosamente");
        // ✅ CORRECCIÓN CRÍTICA: Resetear formulario después de crear campaña exitosamente
        // Esto asegura que el formulario esté limpio para la próxima creación
        if (formikResetFormRef.current) {
          formikResetFormRef.current();
        }
      }

      if (isMounted.current) {
        setSubmitting(false);
        handleClose();
        onSave();
      }
    } catch (err) {
      if (isMounted.current) {
        setSubmitting(false);
      }
      toastError(err);
    }
  };

  // ✅ NUEVO (08/12/2025): Funciones para manejar modal de horarios
  // Nota: Estas funciones deben estar dentro del render de Formik para acceder a values y setFieldValue

  return {
    attachment,
    attachmentFile,
    availableGroups,
    campaignMedia,
    companyId,
    configTab,
    confirmChangeRecurrenceTypeModalOpen,
    confirmChangeScheduledDateModalOpen,
    confirmDeleteAllSchedulesModalOpen,
    confirmDisableHourlyScheduleModalOpen,
    confirmDisableRecurrenceModalOpen,
    confirmModalOpen,
    copiedGroupsData,
    currentCampaignType,
    currentSchedule,
    datePastMessage,
    datePastModalOpen,
    editingScheduleIndex,
    endIndex,
    files,
    filteredGroups,
    formatFileSize,
    formatInvalidMessage,
    formatInvalidModalOpen,
    formikResetFormRef,
    formikSetFieldValueRef,
    groupPage,
    groupsPerPage,
    handleAttachmentClick,
    handleClose,
    handleConfirmAndSave,
    handleConfirmChangeRecurrenceType,
    handleConfirmDisableRecurrence,
    handleFileChange,
    handleNextPage,
    handlePrevPage,
    handleRecurrenceTypeChange,
    handleSave,
    hasInitialized,
    hourlyScheduleFileInput,
    hourlyScheduleMessageTab,
    hourlyScheduleModalOpen,
    hourlyScheduleTimeTouched,
    initialIsRecurring,
    initialRecurrenceInterval,
    initialRecurrenceType,
    initialState,
    initialTipo,
    isCompleted,
    isMounted,
    loading,
    loadingGroups,
    messageTab,
    missingScheduledDateModalOpen,
    paginatedGroups,
    pendingNewHour,
    pendingNewRecurrenceType,
    pendingOldHour,
    pendingOldRecurrenceType,
    pendingRecurrenceTypeChange,
    pendingSaveValues,
    pendingScheduledAt,
    pendingScheduledDateChange,
    pendingSchedulesCount,
    pendingSetFieldValue,
    pendingSubmit,
    processSave,
    saveCampaignRef,
    searchGroupTerm,
    selectedConnection,
    selectedGroups,
    setAttachment,
    setAvailableGroups,
    setCampaignMedia,
    setConfigTab,
    setConfirmChangeRecurrenceTypeModalOpen,
    setConfirmChangeScheduledDateModalOpen,
    setConfirmDeleteAllSchedulesModalOpen,
    setConfirmDisableHourlyScheduleModalOpen,
    setConfirmDisableRecurrenceModalOpen,
    setConfirmModalOpen,
    setCopiedGroupsData,
    setCurrentSchedule,
    setDatePastMessage,
    setDatePastModalOpen,
    setEditingScheduleIndex,
    setFiles,
    setFormatInvalidModalOpen,
    setGroupPage,
    setHasInitialized,
    setHourlyScheduleMessageTab,
    setHourlyScheduleModalOpen,
    setHourlyScheduleTimeTouched,
    setIsCompleted,
    setLoadingGroups,
    setMessageTab,
    setMissingScheduledDateModalOpen,
    setPendingNewHour,
    setPendingNewRecurrenceType,
    setPendingOldHour,
    setPendingOldRecurrenceType,
    setPendingRecurrenceTypeChange,
    setPendingSaveValues,
    setPendingScheduledAt,
    setPendingScheduledDateChange,
    setPendingSchedulesCount,
    setPendingSetFieldValue,
    setPendingSubmit,
    setSearchGroupTerm,
    setSelectedConnection,
    setSelectedGroups,
    setShortTimeMessage,
    setShortTimeModalOpen,
    setSizeExceededModalOpen,
    setWhatsapps,
    shortTimeMessage,
    shortTimeModalOpen,
    sizeExceededModalOpen,
    startIndex,
    toggleGroupSelection,
    totalPages,
    uniqueGroups,
    user,
    validateForm,
    whatsapps,
  };
}
